import { Api } from "@/api"
import Icon from "@/components/Icon"
import Modal from "@/components/Modal"
import BootstrapButton from "@/components/form/BootstrapButton"
import { useLoading } from "@/composition/useLoading"
import type { DocumentUpload, Id, ShipmentLogDocumentKey } from "@/models"
import ExistingDocuments from "@/shared/ExistingDocuments"
import UploadDocumentsWithRecipientsModal from "@/shared/UploadDocumentsWithRecipientsModal"
import { faPlusCircle } from "@fortawesome/free-solid-svg-icons"
import { ref } from "vue"
import { defineComponent, inferProps, ModalTitle, type ReactiveComponent } from "vue-utils"

interface Props {
	title: string
	existingDocuments: DocumentUpload[]
	canUpload: boolean
	type: ShipmentLogDocumentKey
	tfsId: Id

	uploadDocuments(file: File[], recipients: string[]): Promise<void>
	deleteDocument: (document: DocumentUpload) => void

	close(): void
}

const ManageDocuments: ReactiveComponent<Props> = (props) => {
	const uploadingDocs = ref(false)
	const { runAction } = useLoading()

	let recipientSuggestions: string[] = []
	async function openUploadFileDialog() {
		recipientSuggestions = await runAction(Api.tfs.getRecipientSuggestions(props.tfsId))
		uploadingDocs.value = true
	}

	async function handleUpload(files: File[], recipients: string[]) {
		await props.uploadDocuments(files, recipients)
		uploadingDocs.value = false
	}

	return () => (
		<Modal class="flex flex-col" style={{ width: "60rem", height: "36rem" }}>
			<ModalTitle title={props.title} />
			<hr />

			<div class="flex flex-col flex-grow-1 flex-shrink-1 overflow-auto">
				{props.existingDocuments.length === 0 && (
					<ExistingDocuments name="" documents={props.existingDocuments} delete={props.deleteDocument} />
				)}
				{uploadingDocs.value && props.canUpload && (
					<UploadDocumentsWithRecipientsModal
						cancel={() => (uploadingDocs.value = false)}
						existingRecipients={recipientSuggestions}
						upload={handleUpload}
					/>
				)}
				{props.canUpload && (
					<div class={props.existingDocuments.length === 0 ? "mt-2" : "mb-2"}>
						<BootstrapButton color="success" onClick={() => void openUploadFileDialog()}>
							<Icon icon={faPlusCircle} />
							Upload Documents
						</BootstrapButton>
					</div>
				)}
				{props.existingDocuments.length > 0 && (
					<ExistingDocuments name="" documents={props.existingDocuments} delete={props.deleteDocument} />
				)}
			</div>
			<hr />

			<div class="flex justify-end spacing-3">
				<BootstrapButton color="secondary" onClick={props.close}>
					Close
				</BootstrapButton>
			</div>
		</Modal>
	)
}

export default defineComponent(ManageDocuments, inferProps<Props>())
